/**
 * Utility functions for managing tour/feature tracking in localStorage
 */

const TOUR_STORAGE_KEY = 'tour_tracking';

export type TourTrackingConfig = Record<string, boolean>;

/**
 * Tour keys for different features
 */
export const TOUR_KEYS = {
  WORKSPACE_SCHEDULE_RESIZE: 'workspace_schedule_resize',
} as const;

/**
 * Get the tour tracking configuration from localStorage
 */
export function getTourTracking(): TourTrackingConfig {
  try {
    const stored = localStorage.getItem(TOUR_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to read tour tracking from localStorage:', error);
    return {};
  }
}

/**
 * Check if a specific tour has been completed or closed
 */
export function isTourCompleted(tourKey: string): boolean {
  const config = getTourTracking();
  return config[tourKey] === true;
}

/**
 * Mark a tour as completed/closed
 */
export function markTourCompleted(tourKey: string): void {
  try {
    const config = getTourTracking();
    config[tourKey] = true;
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to save tour tracking to localStorage:', error);
  }
}

/**
 * Reset a specific tour (for testing/debugging)
 */
export function resetTour(tourKey: string): void {
  try {
    const config = getTourTracking();
    delete config[tourKey];
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(config));
  } catch (error) {
    console.error('Failed to reset tour in localStorage:', error);
  }
}

/**
 * Clear all tour tracking data
 */
export function clearAllTours(): void {
  try {
    localStorage.removeItem(TOUR_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear tour tracking from localStorage:', error);
  }
}
